// Style inheritance: role base style → named text style chain → node override.
// Named styles (doc.textStyles) are optional; documents without them resolve
// exactly as before ({...doc.styles[role], ...node.style}).

const MAX_DEPTH = 8;

function textStyleChain(doc, id) {
  const chain = [];
  const styles = doc.textStyles || {};
  let current = id, depth = 0;
  const seen = new Set();
  while (current && styles[current] && !seen.has(current) && depth++ < MAX_DEPTH) {
    seen.add(current);
    chain.unshift(styles[current]);
    current = styles[current].basedOn;
  }
  return chain;
}

function resolveStyle(doc, node) {
  const base = (doc.styles && doc.styles[node.role]) || {};
  if (!node.styleRef) return { ...base, ...node.style };
  const out = { ...base };
  for (const named of textStyleChain(doc, node.styleRef)) Object.assign(out, named.style);
  return Object.assign(out, node.style);
}

// Where a single style property comes from, for the inspector's source badges.
function styleSource(doc, node, key) {
  if (node.style && Object.prototype.hasOwnProperty.call(node.style, key)) return { level: "element" };
  if (node.styleRef) {
    const chain = textStyleChain(doc, node.styleRef);
    for (let i = chain.length - 1; i >= 0; i--)
      if (chain[i].style && Object.prototype.hasOwnProperty.call(chain[i].style, key)) {
        const id = Object.keys(doc.textStyles).find((k) => doc.textStyles[k] === chain[i]);
        return { level: "textStyle", id, name: chain[i].name };
      }
  }
  if (doc.styles && doc.styles[node.role] && Object.prototype.hasOwnProperty.call(doc.styles[node.role], key)) return { level: "role", id: node.role };
  return { level: "default" };
}

// Style without the node's own overrides — what "reset" returns a key to.
function inheritedStyle(doc, node) {
  return resolveStyle(doc, { ...node, style: {} });
}

function validTextStyles(textStyles, roles) {
  if (textStyles === undefined) return true;
  if (!textStyles || typeof textStyles !== "object" || Array.isArray(textStyles)) return false;
  const entries = Object.entries(textStyles);
  if (entries.length > 200) return false;
  for (const [id, style] of entries) {
    if (!/^ts:[a-zA-Z0-9_-]{1,100}$/.test(id)) return false;
    if (!style || typeof style !== "object" || typeof style.name !== "string" || !style.name.trim() || style.name.length > 100) return false;
    if (style.basedOn !== undefined && !(typeof style.basedOn === "string" && Object.prototype.hasOwnProperty.call(textStyles, style.basedOn))) return false;
    if (style.role !== undefined && !roles.includes(style.role)) return false;
    if (!style.style || typeof style.style !== "object" || Array.isArray(style.style)) return false;
  }
  // No inheritance cycles.
  for (const id of Object.keys(textStyles)) {
    const seen = new Set();
    let current = id;
    while (current) {
      if (seen.has(current)) return false;
      seen.add(current);
      current = textStyles[current].basedOn;
    }
  }
  return true;
}

module.exports = { resolveStyle, styleSource, inheritedStyle, textStyleChain, validTextStyles };
